// Erasure holes live in pattern coordinates and follow its transform and mirror.
// Erasing has no operation-count quota. Request byte limits still protect APIs;
// never truncate a customer's mask when saving or restoring a design.
export const PATTERN_BOUNDS={x:-100,y:-150,width:300,height:450};
const finite=value=>typeof value==='number'&&Number.isFinite(value);
const round=value=>Math.round(value*1000)/1000;
const intersects=({x,y,r,rx=r,ry=r})=>Math.hypot((x-Math.max(-100,Math.min(200,x)))/rx,(y-Math.max(-150,Math.min(300,y)))/ry)<=1;

export function normalizePatternCuts(input){
  if(!Array.isArray(input))return[];
  const seen=new Set(),cuts=[];
  for(const value of input){
    if(!value||!finite(value.x)||!finite(value.y)||!finite(value.r)||value.x< -300||value.x>400||value.y< -350||value.y>500||value.r<.1||value.r>200)continue;
    const cut={x:round(value.x),y:round(value.y),r:round(value.r)};
    if(finite(value.rx)&&finite(value.ry)&&value.rx>=.001&&value.rx<=200&&value.ry>=.001&&value.ry<=200&&(round(value.rx)!==cut.r||round(value.ry)!==cut.r))Object.assign(cut,{rx:round(value.rx),ry:round(value.ry)});
    const key=`${cut.x}:${cut.y}:${cut.r}:${cut.rx||cut.r}:${cut.ry||cut.r}`;
    if(!seen.has(key)&&intersects(cut)){seen.add(key);cuts.push(cut);}
  }
  return cuts;
}

export function patternPointCovered(layer,localPoint){
  if(!localPoint||!finite(localPoint.x)||!finite(localPoint.y))return false;
  return (layer.patternCuts||[]).some(c=>Math.hypot((localPoint.x-c.x)/(c.rx||c.r),(localPoint.y-c.y)/(c.ry||c.r))<=1);
}

export function isLocallyErasableLayer(layer){
  return !!layer&&(['sticker','stroke'].includes(layer.kind)||(layer.kind==='pattern'&&layer.type!=='dots'));
}

export function layerErasePoint(layer,point){
  const scale=finite(layer.scale)&&layer.scale>0?layer.scale:1;
  const radians=-(layer.rotation||0)*Math.PI/180,dx=point.x-layer.x,dy=point.y-layer.y;
  const centered=layer.kind==='sticker';
  let x=(centered?0:50)+(dx*Math.cos(radians)-dy*Math.sin(radians))/(scale*(centered?layer.scaleX||1:1));
  const y=(centered?0:75)+(dx*Math.sin(radians)+dy*Math.cos(radians))/(scale*(centered?layer.scaleY||1:1));
  if(layer.patternFlipX)x=100-x;
  return{x,y};
}

function cutContains(outer,inner){
  if(!outer.rx&&!inner.rx)return Math.hypot(outer.x-inner.x,outer.y-inner.y)+inner.r<=outer.r+.001;
  // A conservative ellipse containment bound never removes any earlier hole.
  const rx=outer.rx||outer.r,ry=outer.ry||outer.r;
  return Math.hypot((inner.x-outer.x)/rx,(inner.y-outer.y)/ry)+Math.max((inner.rx||inner.r)/rx,(inner.ry||inner.r)/ry)<=1+1e-9;
}
function cutArtwork(value,point,radius,axes){
  const cut=normalizePatternCuts([{...point,r:Math.max(.1,Math.min(200,radius)),...axes}])[0];
  if(!cut)return value;
  const old=normalizePatternCuts(value.patternCuts);
  if(old.some(c=>cutContains(c,cut)))return value;
  const retained=old.filter(c=>!cutContains(cut,c));
  return{...value,patternCuts:[...retained,cut]};
}

export function eraseLayerAt(layer,point,radius){
  if(!isLocallyErasableLayer(layer)||layer.visible===false||layer.opacity===0||!point||!finite(point.x)||!finite(point.y)||!finite(radius)||radius<=0)return layer;
  const scale=finite(layer.scale)&&layer.scale>0?layer.scale:1;
  const axes=layer.kind==='sticker'&&((layer.scaleX||1)!==1||(layer.scaleY||1)!==1||radius/scale<.1)?{rx:Math.max(.001,Math.min(200,radius/(scale*(layer.scaleX||1)))),ry:Math.max(.001,Math.min(200,radius/(scale*(layer.scaleY||1))))}:undefined;
  return cutArtwork(layer,layerErasePoint(layer,point),radius/scale,axes);
}

export function eraseFrenchAt(french,point,radius){
  if(!french||!point||!finite(point.x)||!finite(point.y)||!finite(radius)||radius<=0)return french;
  return cutArtwork(french,point,radius);
}

// Keep the ordinary-pattern API for existing callers and saved designs.
export function erasePatternAt(layer,point,radius){
  return layer?.kind==='pattern'?eraseLayerAt(layer,point,radius):layer;
}
